// Security tab — change password + active sessions with revoke.

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Loading } from "@/components/ui/Loading";
import { ApiError } from "@/lib/api";
import { clearTokens } from "@/lib/tokens";
import { confirm } from "@/stores/confirmStore";
import { toast } from "@/stores/toastStore";
import { useDict, fmt } from "@/i18n/LocaleProvider";
import {
  useChangePassword,
  useRevokeAllSessions,
  useRevokeSession,
  useSessions,
} from "@/hooks/useProfile";

interface Message {
  type: "ok" | "err";
  text: string;
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError)
    return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
  if (err instanceof Error) return err.message;
  return String(err);
}

export function SecurityTab() {
  const t = useDict().app.profile.security;
  const sessions = useSessions();
  const changePassword = useChangePassword();
  const revokeSession = useRevokeSession();
  const revokeAll = useRevokeAllSessions();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [msg, setMsg] = useState<Message | null>(null);

  async function handleChangePassword() {
    setMsg(null);
    if (next !== confirmPwd) {
      setMsg({ type: "err", text: t.noMatch });
      return;
    }
    if (next.length < 8) {
      setMsg({
        type: "err",
        text: t.tooShort,
      });
      return;
    }
    try {
      await changePassword.mutateAsync({
        current_password: current,
        new_password: next,
      });
      setMsg({
        type: "ok",
        text: t.changed,
      });
      setCurrent("");
      setNext("");
      setConfirmPwd("");
    } catch (e) {
      setMsg({ type: "err", text: formatErr(e) });
    }
  }

  async function handleRevokeSession(id: string) {
    const ok = await confirm({
      title: t.revokeSessionTitle,
      message: t.revokeSessionMsg,
      variant: "danger",
      confirmLabel: t.revoke,
    });
    if (!ok) return;
    try {
      await revokeSession.mutateAsync(id);
      toast.success(t.sessionRevoked);
    } catch (e) {
      toast.error(`${t.failed} ${formatErr(e)}`);
    }
  }

  async function handleRevokeAll() {
    const ok = await confirm({
      title: t.revokeAllTitle,
      message: t.revokeAllMsg,
      variant: "danger",
      confirmLabel: t.revokeAll,
    });
    if (!ok) return;
    try {
      await revokeAll.mutateAsync();
      clearTokens();
      window.location.reload();
    } catch (e) {
      toast.error(`${t.failed} ${formatErr(e)}`);
    }
  }

  const list = sessions.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* Change password */}
      <Card>
        <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em] mb-3">
          {t.changePassword}
        </div>
        <Input
          label={t.currentPassword}
          value={current}
          onChange={setCurrent}
          type="password"
        />
        <Input
          label={t.newPassword}
          value={next}
          onChange={setNext}
          type="password"
        />
        <Input
          label={t.confirmNewPassword}
          value={confirmPwd}
          onChange={setConfirmPwd}
          type="password"
        />
        {msg && (
          <div
            className="px-3 py-2 rounded font-mono text-[11px] mb-[10px]"
            style={{
              background: msg.type === "err" ? "rgb(var(--color-danger-bg))" : "rgb(var(--color-success-bg))",
              border: `1px solid ${msg.type === "err" ? "rgb(var(--color-danger) / 0.4)" : "rgb(var(--color-success) / 0.4)"}`,
              color: msg.type === "err" ? "rgb(var(--color-danger))" : "rgb(var(--color-success))",
            }}
          >
            {msg.text}
          </div>
        )}
        <Button
          variant="primary"
          onClick={handleChangePassword}
          disabled={changePassword.isPending || !current || !next || !confirmPwd}
        >
          {changePassword.isPending ? t.busy : t.changePasswordBtn}
        </Button>
      </Card>

      {/* Sessions */}
      <Card>
        <div className="flex justify-between items-center mb-3">
          <div className="font-mono text-[11px] text-muted uppercase tracking-[0.08em]">
            {fmt(t.activeSessions, { n: list.length })}
          </div>
          <Button
            variant="danger"
            small
            onClick={handleRevokeAll}
            disabled={list.length === 0}
          >
            {t.revokeAllBtn}
          </Button>
        </div>
        {sessions.isLoading ? (
          <Loading text={t.loading} />
        ) : list.length === 0 ? (
          <div className="text-dim text-center p-3 font-mono text-[11px]">
            {t.noSessions}
          </div>
        ) : (
          list.map((s) => {
            const ua = s.user_agent || t.unknown;
            const uaShort = ua.length > 80 ? ua.substring(0, 80) + "..." : ua;
            return (
              <div
                key={s.id}
                className="flex justify-between items-center py-[10px] border-b border-stroke"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] text-content truncate">
                    {uaShort}
                  </div>
                  <div className="font-mono text-[10px] text-muted mt-[3px]">
                    {s.ip_address || "?"} · {t.createdLabel}{" "}
                    {s.created_at
                      ? new Date(s.created_at).toLocaleString()
                      : "?"}
                    {s.last_used_at &&
                      " · " + t.lastUsedLabel + " " +
                        new Date(s.last_used_at).toLocaleString()}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  small
                  onClick={() => handleRevokeSession(s.id)}
                >
                  {t.revoke}
                </Button>
              </div>
            );
          })
        )}
      </Card>
    </div>
  );
}
